import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn, CoinIcon, DetailBackButton, ErrorState, PageMeta, PageShell, Skeleton } from '@/shared';
import { extractErrorCode } from '@/shared/api/error-handler';
import { resolveErrorDisplay } from '@/shared/lib/query/error-display';
import { formatLocalizedNumber, getPriceFractionDigits } from '@/shared/lib/utils/format';
import { signedFixed } from '@/shared/lib/utils/price-tone';
import { t } from '@/i18n';
import NotFound from '@/pages/not-found/NotFound';

import {
  KIMCHI_DAILY_NOT_FOUND_CODE,
  KIMCHI_DAILY_TOP,
  formatKimchiDailyInstant,
  decideKimchiDayNoIndex,
  isValidKimchiDay,
  kimchiDayPath,
  kimchiKstToday,
  representativeGranularity,
  shiftKimchiDay,
} from '@/entities/kimchi-premium/lib/daily-page';
import { kimchiPremiumDailyQueryOptions } from '@/entities/kimchi-premium/model/query-options';

import type { KimchiPremiumCoinDaily, KimchiPremiumDaily } from '@/entities/kimchi-premium/types';

/**
 * TRAD-3709 P2 — 김치프리미엄 «일간» 페이지 `/kimchi-premium/YYYY-MM-DD`(마케팅 4주 계획 ⑤⑥ · backend TRAD-3719).
 * 검색에 걸리는 고유 URL·title·description·canonical. prerender 는 어제까지 14일(scripts/lib/kimchi-day-routes.mjs) — 당일은 partial 이라 런타임 + noindex.
 *
 * 기준 화면(UI 룰 1 — 새 레이아웃 0):
 *   골격·이전/다음·설치 버튼 = 캘린더 일자 페이지 `pages/financial-calendar/DayPage.tsx`(DetailBackButton · h1 text-xl font-bold · nav)  — 설치 버튼은 전역 푸터(web_footer)만(11/01 중복 제거)
 *   표 = `KimchiPremium.tsx` 의 grid-cols-4 표(헤더 bg-card text-xs · 행 px-4 py-2.5 border-b · tabular-nums · 프리미엄 색 price-rise/fall).
 * 전일/익일 링크는 **데이터 있는 날만 활성**(팀장 보강 ②) — 이웃 날짜를 같은 쿼리로 미리 당겨 404 면 비활성 span.
 */

/** KimchiPremium.tsx 와 같은 톤(TRAD-3670 — 0 은 중립). */
const premiumTone = (rate: number) => {
  if (rate > 0) return 'text-price-rise';
  if (rate < 0) return 'text-price-fall';
  return 'text-muted-foreground';
};

const formatPrice = (price: number, isKrw: boolean) => {
  const digits = getPriceFractionDigits(price);
  const opts = { maximumFractionDigits: digits, minimumFractionDigits: digits };
  return isKrw ? formatLocalizedNumber(price, opts) : `$${formatLocalizedNumber(price, opts)}`;
};

const DayNavLink = ({ to, enabled, children }: { to: string; enabled: boolean; children: React.ReactNode }) =>
  enabled ? (
    <Link to={to} className="inline-flex items-center gap-1 hover:underline">
      {children}
    </Link>
  ) : (
    <span aria-disabled="true" className="inline-flex items-center gap-1 text-muted-foreground cursor-not-allowed">
      {children}
    </span>
  );

const PinnedCard = ({ coin }: { coin: KimchiPremiumCoinDaily }) => (
  <div className="rounded-lg border border-border bg-card p-4" data-testid={`kimchi-daily-pinned-${coin.coinCode}`}>
    <div className="flex items-center gap-2 text-sm font-medium text-foreground">
      <CoinIcon code={coin.coinCode} size="sm" />
      {coin.coinCode}
    </div>
    <div className={cn('mt-2 text-2xl font-bold tabular-nums', premiumTone(coin.closePremiumRate))}>
      {signedFixed(coin.closePremiumRate, 2)}%
    </div>
    <div className="mt-1 text-xs text-muted-foreground">{t('kimchi_premium.day.card.close')}</div>
    <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
      <dt className="text-muted-foreground">{t('kimchi_premium.day.card.avg')}</dt>
      <dd className="text-right tabular-nums text-foreground">{signedFixed(coin.avgPremiumRate, 2)}%</dd>
      <dt className="text-muted-foreground">{t('kimchi_premium.day.card.range')}</dt>
      <dd className="text-right tabular-nums text-foreground">
        {signedFixed(coin.maxPremiumRate, 2)}% / {signedFixed(coin.minPremiumRate, 2)}%
      </dd>
    </dl>
    <p className="mt-2 text-xs text-muted-foreground">
      {t('kimchi_premium.day.card.price', {
        krw: formatPrice(coin.closeDomesticPrice, true),
        usd: formatPrice(coin.closeBinancePrice, false),
      })}
    </p>
  </div>
);

const DailyBody = ({ data }: { data: KimchiPremiumDaily }) => {
  const granularity = representativeGranularity(data);
  const asOf = formatKimchiDailyInstant(data.asOf, granularity);
  const fx = data.pinned[0]?.closeExchangeRate ?? data.top[0]?.closeExchangeRate;
  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 mb-4">
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-xs text-muted-foreground">{t('kimchi_premium.day.market_avg')}</div>
          <div
            className={cn('mt-1 text-2xl font-bold tabular-nums', premiumTone(data.marketAveragePremiumRate))}
            data-testid="kimchi-daily-market-avg"
          >
            {signedFixed(data.marketAveragePremiumRate, 2)}%
          </div>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-xs text-muted-foreground">{t('kimchi_premium.day.coin_count')}</div>
          <div className="mt-1 text-2xl font-bold tabular-nums text-foreground">
            {formatLocalizedNumber(data.coinCount)}
          </div>
        </div>
        <div className="rounded-lg border border-border bg-card p-4 col-span-2 sm:col-span-1">
          <div className="text-xs text-muted-foreground">{t('kimchi_premium.day.as_of', { time: asOf })}</div>
          {fx != null && (
            <div className="mt-1 text-sm text-foreground">
              {t('kimchi.exchange_rate', { rate: formatLocalizedNumber(fx) })}
            </div>
          )}
          {data.partial && (
            <span
              data-testid="kimchi-daily-partial"
              className="mt-2 inline-block rounded px-1.5 py-0.5 text-xs font-medium bg-secondary text-foreground"
            >
              {t('kimchi_premium.day.partial_badge')}
            </span>
          )}
        </div>
      </div>

      <h2 className="text-base font-bold text-foreground mb-2">{t('kimchi_premium.day.pinned_heading')}</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 mb-6">
        {data.pinned.map(coin => (
          <PinnedCard key={coin.coinCode} coin={coin} />
        ))}
      </div>

      <h2 className="text-base font-bold text-foreground mb-2">
        {t('kimchi_premium.day.top_heading', { count: String(Math.min(KIMCHI_DAILY_TOP, data.top.length)) })}
      </h2>
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="grid grid-cols-4 gap-2 px-4 py-2.5 bg-card text-xs font-medium text-muted-foreground border-b border-border">
          <div>{t('kimchi_premium.table.coin')}</div>
          <div className="text-right">{t('kimchi_premium.day.table.avg')}</div>
          <div className="text-right">{t('kimchi_premium.day.table.range')}</div>
          <div className="text-right">{t('kimchi_premium.day.table.close')}</div>
        </div>
        {data.top.length === 0 ? (
          <div className="px-4 py-8 text-center text-muted-foreground text-sm">{t('kimchi_premium.table.empty')}</div>
        ) : (
          data.top.map(coin => (
            <div
              key={coin.coinCode}
              className="grid grid-cols-4 gap-2 px-4 py-2.5 border-b border-border last:border-b-0"
              data-testid="kimchi-daily-row"
            >
              <div className="font-medium text-foreground text-sm flex items-center gap-2 min-w-0">
                <CoinIcon code={coin.coinCode} size="sm" />
                <span className="truncate">{coin.coinCode}</span>
              </div>
              <div className={cn('text-right text-sm font-medium tabular-nums', premiumTone(coin.avgPremiumRate))}>
                {signedFixed(coin.avgPremiumRate, 2)}%
              </div>
              <div className="text-right text-xs text-muted-foreground tabular-nums self-center">
                {signedFixed(coin.maxPremiumRate, 2)}% / {signedFixed(coin.minPremiumRate, 2)}%
              </div>
              <div className={cn('text-right text-sm tabular-nums', premiumTone(coin.closePremiumRate))}>
                {signedFixed(coin.closePremiumRate, 2)}%
              </div>
            </div>
          ))
        )}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{t('kimchi_premium.day.granularity_note')}</p>
    </>
  );
};

const KimchiPremiumDailyPage = () => {
  const { date } = useParams<{ date: string }>();
  const valid = isValidKimchiDay(date);
  const today = kimchiKstToday();
  const day = valid ? date : '';
  const prev = valid ? shiftKimchiDay(day, -1) : '';
  const next = valid ? shiftKimchiDay(day, 1) : '';

  // 🔴 훅은 조건 없이 — 날짜가 무효면 enabled 로 끈다(NotFound 는 아래에서).
  const dayQuery = useQuery({ ...kimchiPremiumDailyQueryOptions.get(day, day === today), enabled: valid });
  const prevQuery = useQuery({ ...kimchiPremiumDailyQueryOptions.get(prev, false), enabled: valid });
  // 익일은 오늘까지만 — 미래는 서버가 404 라 요청하지 않는다.
  const nextQuery = useQuery({
    ...kimchiPremiumDailyQueryOptions.get(next, next === today),
    enabled: valid && next <= today,
  });

  if (!valid) return <NotFound />;

  const { data, isLoading, isError, error, refetch } = dayQuery;
  const notFound = isError && extractErrorCode(error) === KIMCHI_DAILY_NOT_FOUND_CODE;
  // TRAD-3656 규약 — 데이터가 있으면 막지 않는다(이 페이지는 재조회가 드물어 사실상 blocking 만 쓰이지만 게이트 함수를 거친다).
  const errorDisplay = resolveErrorDisplay({ isError: isError && !notFound, hasData: !!data });
  const title = t('kimchi_premium.day.meta_title', { date: day });

  return (
    <>
      <PageMeta
        title={title}
        description={t('kimchi_premium.day.meta_description', { date: day })}
        path={kimchiDayPath(day)}
        noIndex={decideKimchiDayNoIndex({ day, today, data, notFound })}
        breadcrumb={[
          { name: t('kimchi_premium.page.heading'), path: '/kimchi-premium' },
          { name: title, path: kimchiDayPath(day) },
        ]}
      />
      <PageShell width="data" padding="comfortable">
        <DetailBackButton to="/kimchi-premium" icon="chevron" label={t('kimchi_premium.page.heading')} />
        <h1 className="text-xl font-bold mb-4">{title}</h1>

        <nav className="flex flex-wrap items-center gap-3 mb-4 text-sm" aria-label={t('kimchi_premium.page.heading')}>
          <DayNavLink to={kimchiDayPath(prev)} enabled={prevQuery.isSuccess}>
            <ChevronLeft className="size-4" aria-hidden="true" />
            {t('kimchi_premium.day.prev')}
          </DayNavLink>
          <DayNavLink to={kimchiDayPath(next)} enabled={nextQuery.isSuccess}>
            {t('kimchi_premium.day.next')}
            <ChevronRight className="size-4" aria-hidden="true" />
          </DayNavLink>
        </nav>

        {isLoading ? (
          <div className="space-y-3" data-testid="kimchi-daily-loading">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : notFound ? (
          <div className="rounded-lg border border-border px-4 py-10 text-center" data-testid="kimchi-daily-empty">
            <p className="text-sm font-medium text-foreground">{t('kimchi_premium.day.empty_title', { date: day })}</p>
            <p className="mt-1 text-xs text-muted-foreground">{t('kimchi_premium.day.empty_body')}</p>
          </div>
        ) : errorDisplay === 'blocking' || !data ? (
          <div className="border border-border rounded-lg overflow-hidden">
            <ErrorState size="md" onRetry={() => void refetch()} />
          </div>
        ) : (
          <DailyBody data={data} />
        )}
      </PageShell>
    </>
  );
};

export default KimchiPremiumDailyPage;
